import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { API, SettingDTO } from '../api/api';
import { firstValueFrom, Observable } from 'rxjs';
import { Permission } from '../api/permission';
import { ErrorService } from '../error.service';
import { ModalService } from '../gui/modal/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { PermissionService } from '../permission.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StringTransformationService } from '../transformation-helper/string-transformation.service';

@Injectable({
  providedIn: 'root',
})
export class WebSettingsService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly stringTransformation = inject(StringTransformationService);

  public loading: boolean;
  public settings = signal<Record<string, SettingDTO>>({});

  constructor(
    private readonly api: API,
    private readonly errorService: ErrorService,
    private readonly modalService: ModalService,
    private readonly translate: TranslateService,
    public readonly permissionService: PermissionService
  ) {
    this.loading = false;
  }

  public async refreshFromServer() {
    this.loading = true;

    let obs: Observable<any>;
    // check permission
    if (this.permissionService.hasPermission(Permission.client_setting_list)) {
      obs = this.api.shared_Setting_getClientSettings();
    } else {
      obs = this.api.shared_Setting_getSettings();
    }
    try {
      const result = await firstValueFrom(
        obs.pipe(
          take(1),
          takeUntilDestroyed(this.destroyRef),
          finalize(() => {
            this.loading = false;
          })
        )
      );
      const settingsInCamelCase: Record<string, SettingDTO> = {};
      for (const key of Object.keys(result)) {
        // convert keys to camel case
        settingsInCamelCase[this.stringTransformation.toCamelCase(key)] = result[key];
      }
      this.settings.set(settingsInCamelCase);
    } catch (error) {
      this.errorService.errorHandler(error);
    }
  }

  public async setSetting(settingID: string, value: string): Promise<SettingDTO> {
    // check permission
    if (!this.permissionService.hasPermission(Permission.client_setting_edit)) {
      this._showPermissionError(Permission.client_setting_edit);
      return Promise.reject(new Error('No Permission to edit Setting'));
    }

    try {
      const newSetting = await firstValueFrom(
        this.api
          .shared_Setting_editSetting({
            setting: this.stringTransformation.toSnakeCase(settingID),
            value,
            override: true,
          })
          .pipe(take(1))
      );
      this.settings.update(settings => {
        settings[settingID] = newSetting;
        return settings;
      });
      return newSetting;
    } catch (error) {
      this.errorService.errorHandler(error);
      return Promise.reject(new Error(error));
    }
  }

  public async deleteSetting(settingID: string): Promise<string> {
    // check permission
    if (!this.permissionService.hasPermission(Permission.client_setting_delete)) {
      this._showPermissionError(Permission.client_setting_delete);
      return Promise.reject(new Error('No Permission to delete Setting'));
    }

    try {
      const newValue = await firstValueFrom(
        this.api.shared_Setting_deleteSetting(this.stringTransformation.toSnakeCase(settingID)).pipe(take(1))
      );
      this.settings.update(newSettings => {
        newSettings[settingID].value = newValue;
        return newSettings;
      });
      return newValue;
    } catch (error) {
      this.errorService.errorHandler(error);
      return Promise.reject(new Error(error));
    }
  }

  public getSetting(settingID: string): SettingDTO {
    return this.settings()?.[settingID];
  }

  private _showPermissionError(permission: Permission): void {
    const messageTextMarker =
      permission === Permission.client_setting_edit
        ? 'lang.clientManagement.settings.permissionError.edit.text'
        : 'lang.clientManagement.settings.permissionError.delete.text';

    this.modalService
      .alert(
        this.translate.instant('lang.clientManagement.settings.permissionError.headline'),
        this.translate.instant(messageTextMarker),
        this.translate.instant('lang.globals.ok')
      )
      .then(() => {});
  }
}
